import React from "react";
import {
  Box,
  Flex,
  SimpleGrid,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Heading,
  VStack,
  HStack,
  Icon,
  Badge,
  Avatar,
  Container,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Grid,
  GridItem,
  Button,
  ButtonGroup,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import {
  FiHome,
  FiMapPin,
  FiLayers,
  FiFileText,
  FiClock,
  FiUser,
  FiTool,
  FiActivity,
  FiBarChart,
  FiInfo,
} from "react-icons/fi";
import { MdApartment, MdLocationCity } from "react-icons/md";
import { BsBuilding } from "react-icons/bs";
import Link from "../../../../../components/Link";
import ShortenedText from "../../../../../components/ShortenedText";
import { InfoOutlineIcon, TimeIcon } from "@chakra-ui/icons";

import { useLoaderData } from "react-router-dom";
import client from "../../../../../helpers/Client";
import {
  translateKind,
  translateProjectKind,
} from "../../../../../shared/libs/translate";
import urlToId from "../../../../../helpers/UrlToId";
import formatDateTable from "../../../../../helpers/FormatDateTable";
import { TicketState } from "../../../../../components/States";
import formatDate from "../../../../../helpers/formatDate";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Modern Info Card Component
function ModernInfoCard({ title, value, icon, color = "blue", delay = 0, children }) {
  const cardBg = "rgba(255, 255, 255, 0.8)";
  const borderColor = "gray.200";
  const textColor = "gray.600";
  
  return (
    <Card
      bg={cardBg}
      backdropFilter="blur(20px)"
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{
        transform: "translateY(-2px)",
        boxShadow: "lg",
        borderColor: `${color}.300`,
      }}
      animation={`${slideUp} 0.6s ease-out ${delay}s both`}
    >
      <CardBody p={4}>
        <VStack spacing={3} align="stretch">
          <HStack justify="space-between">
            <Icon as={icon} boxSize={5} color={`${color}.500`} />
          </HStack>
          <VStack align="stretch" spacing={1}>
            <Text fontSize="xs" color={textColor} fontWeight="medium">
              {title}
            </Text>
            <Box fontSize="sm" fontWeight="semibold" color={`${color}.600`}>
              {children || (
                <Text>{value || "--"}</Text>
              )}
            </Box>
          </VStack>
        </VStack>
      </CardBody>
    </Card>
  );
}

// Unit Stats Component
function UnitStats({ tickets }) {
  const totalTickets = tickets.length;
  const openTickets = tickets.filter(t => t.state !== 'closed').length;
  const closedTickets = tickets.filter(t => t.state === 'closed').length;
  const recentTickets = tickets.filter(t => {
    const created = new Date(t.created_at);
    const now = new Date();
    const diffTime = Math.abs(now - created);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30;
  }).length;

  const cardBg = "rgba(255, 255, 255, 0.9)";
  const borderColor = "gray.200";

  return (
    <Card
      bg={cardBg}
      backdropFilter="blur(20px)"
      border="1px solid"
      borderColor={borderColor}
      borderRadius="2xl"
      overflow="hidden"
      animation={`${fadeIn} 0.8s ease-out 0.1s both`}
    >
      <CardHeader>
        <HStack spacing={3}>
          <Icon as={FiBarChart} color="blue.500" boxSize={6} />
          <Heading size="md">İş Emri İstatistikleri</Heading>
        </HStack>
      </CardHeader>
      <CardBody pt={0}>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
          <Stat>
            <StatLabel color="gray.500" fontSize="xs">Toplam İş Emri</StatLabel>
            <StatNumber color="blue.600" fontSize="2xl">{totalTickets}</StatNumber>
            <StatHelpText color="gray.400">Tüm zamanlar</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel color="gray.500" fontSize="xs">Açık İş Emirleri</StatLabel>
            <StatNumber color="orange.600" fontSize="2xl">{openTickets}</StatNumber>
            <StatHelpText color="gray.400">Devam eden</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel color="gray.500" fontSize="xs">Tamamlanan</StatLabel>
            <StatNumber color="green.600" fontSize="2xl">{closedTickets}</StatNumber>
            <StatHelpText color="gray.400">Kapatılan</StatHelpText>
          </Stat>
          <Stat>
            <StatLabel color="gray.500" fontSize="xs">Son 30 Gün</StatLabel>
            <StatNumber color="purple.600" fontSize="2xl">{recentTickets}</StatNumber>
            <StatHelpText color="gray.400">Yeni işler</StatHelpText>
          </Stat>
        </SimpleGrid>
      </CardBody>
    </Card>
  );
}

export async function unitDetailLoader({ params }) {
  const response = await client.get(`/units/${params.unitID}/`);
  const response2 = await client.get(
    `/tickets/?page_size=100000&assigned_to=&is_unassigned=&number=&customer=&unit=${params.unitID}`
  );
  return [response.data, response2.data];
}

export default function UnitDetail() {
  const [unit, unitTicket] = useLoaderData();
  
  const bgGradient = "linear(135deg, blue.50 0%, purple.50 25%, pink.50 50%, orange.50 75%, yellow.50 100%)";
  
  // Define color mode values at component level
  const headerBg = "gray.50";
  const headerColor = "gray.700";
  const hoverBg = "gray.50";
  const borderColor = "gray.200";
  
  const columns = [
    {
      title: "İŞ TİPİ",
      dataIndex: "kind",
      render: (text) => (
        <Badge colorScheme="blue" variant="subtle" borderRadius="full">
          {translateKind(text)}
        </Badge>
      ),
    },
    {
      title: "Başlık",
      dataIndex: "body",
      render: (text, record) => (
        <Flex maxWidth={"400px"} flexDirection="column" gap={2}>
          <Link to={`/is-emri/${urlToId(record.url)}`} fontWeight="600" whiteSpace={'wrap'}>
            <ShortenedText text={text} maxLength={80} />
          </Link>
          <Flex gap={"10px"} fontSize="sm" color="gray.500">
            <Text as="span" whiteSpace="nowrap">
              #{record.number}
            </Text>
            <Text as="span" whiteSpace="nowrap">
              <TimeIcon mb={"2px"} mr={"2px"} />
              {formatDate(record.created_at)}
            </Text>
            <Text as="span" whiteSpace="nowrap">
              <InfoOutlineIcon mb={"2px"} mr={"2px"} />
              {record.full_name}
            </Text>
          </Flex>
        </Flex>
      ),
    },
    {
      title: "ÜNİTE BİLGİSİ",
      dataIndex: "related_unit",
      render: (text) => (
        <Text style={{ whiteSpace: "nowrap" }}>{text ? text?.name : "--"}</Text>
      ),
    },
    {
      title: "Bayi",
      dataIndex: "assigned_to",
      render: (text) => (
        <Text style={{ whiteSpace: "nowrap" }}>{text ? text?.name : "--"}</Text>
      ),
    },
    {
      title: "Oluşturulma Tarihi",
      dataIndex: "created_at",
      render: (text) => {
        return <Text>{formatDateTable(text)}</Text>;
      },
    },
    {
      title: "Durum",
      dataIndex: "state",
      render: (state) => <TicketState state={state} />,
    },
  ];
  
  return (
    <Box
      minH="100vh"
      bgGradient={bgGradient}
      position="relative"
      overflow="hidden"
    >
      {/* Background Pattern */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        opacity={0.1}
        backgroundImage="radial-gradient(circle at 25% 25%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.2) 0%, transparent 50%)"
        pointerEvents="none"
      />
      
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          {/* Header Section */}
          <Card
            bg={"rgba(255, 255, 255, 0.9)"}
            backdropFilter="blur(20px)"
            border="1px solid"
            borderColor={"gray.200"}
            borderRadius="2xl"
            overflow="hidden"
            animation={`${fadeIn} 0.6s ease-out`}
          >
            <CardHeader>
              <Flex justify="space-between" align="center">
                <HStack spacing={3}>
                  <Avatar
                    size="md"
                    icon={<Icon as={BsBuilding} />}
                    bg="blue.500"
                    color="white"
                  />
                  <VStack align="start" spacing={0}>
                    <Heading size="md" color="blue.600">
                      Ünite: {unit.name}
                    </Heading>
                    <Text fontSize="sm" color="gray.500">
                      Ünite Detay Sayfası
                    </Text>
                  </VStack>
                </HStack>
                <Badge colorScheme="green" variant="subtle" borderRadius="full" px={3} py={1}>
                  Aktif Ünite
                </Badge>
              </Flex>
            </CardHeader>
          </Card>
          
          {/* Unit Info Cards */}
          <Card
            bg={"rgba(255, 255, 255, 0.9)"}
            backdropFilter="blur(20px)"
            border="1px solid"
            borderColor={"gray.200"}
            borderRadius="2xl"
            overflow="hidden"
            animation={`${fadeIn} 0.8s ease-out 0.2s both`}
          >
            <CardHeader>
              <HStack spacing={3}>
                <Icon as={FiInfo} color="blue.500" boxSize={6} />
                <Heading size="md">Ünite Bilgileri</Heading>
              </HStack>
            </CardHeader>
            <CardBody pt={0}>
              <SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={4}>
                <ModernInfoCard
                  title="Proje"
                  icon={FiHome}
                  color="blue"
                  delay={0.1}
                >
                  <Link 
                    to={`/proje/${urlToId(unit.project.url)}/`}
                    color="blue.600"
                    fontWeight="semibold"
                    _hover={{ color: "blue.800" }}
                  >
                    {unit.project.name}
                  </Link>
                </ModernInfoCard>
                <ModernInfoCard
                  title="Ünite Numarası"
                  value={unit.apt_num}
                  icon={MdApartment}
                  color="purple"
                  delay={0.2}
                />
                <ModernInfoCard
                  title="Proje Tipi"
                  value={translateProjectKind(unit.project.kind)}
                  icon={FiLayers}
                  color="green"
                  delay={0.3}
                />
                <ModernInfoCard
                  title="Bölge"
                  value={unit.project.district_info.name}
                  icon={MdLocationCity}
                  color="orange"
                  delay={0.4}
                />
                <ModernInfoCard
                  title="Adres"
                  value={unit.project.address}
                  icon={FiMapPin}
                  color="teal"
                  delay={0.5}
                />
              </SimpleGrid>
            </CardBody>
          </Card>
          
          {/* Statistics */}
          <UnitStats tickets={unitTicket.results} />
          
          {/* Tickets Table */}
          <Card
            bg={"rgba(255, 255, 255, 0.9)"}
            backdropFilter="blur(20px)"
            border="1px solid"
            borderColor={"gray.200"}
            borderRadius="2xl"
            overflow="hidden"
            animation={`${fadeIn} 1s ease-out 0.4s both`}
          >
            <CardHeader>
              <HStack spacing={3}>
                <Icon as={FiFileText} color="blue.500" boxSize={6} />
                <Heading size="md">Üniteye Ait İş Emirleri</Heading>
                <Badge colorScheme="blue" variant="subtle" borderRadius="full">
                  {unitTicket.results.length} İş Emri
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody pt={0}>
              <Box overflowX="auto">
                <TableContainer whiteSpace="wrap">
                  <Table variant="simple" size="md">
                    <Thead>
                      <Tr>
                        {columns.map((column) => (
                          <Th 
                            key={column.dataIndex}
                            bg={headerBg}
                            color={headerColor}
                            fontSize="xs"
                            fontWeight="bold"
                            textTransform="uppercase"
                            letterSpacing="wider"
                          >
                            {column.title}
                          </Th>
                        ))}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {unitTicket.results.map((t, index) => (
                        <Tr 
                          key={t.number}
                          _hover={{
                            bg: hoverBg,
                          }}
                          transition="all 0.2s"
                        >
                          {columns.map((column) => (
                            <Td 
                              key={column.dataIndex}
                              borderColor={borderColor}
                            >
                              {column.render ? (
                                column.render(t[column.dataIndex], t)
                              ) : (
                                <span>{t[column.dataIndex]}</span>
                              )}
                            </Td>
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  );
}
